import { AriaAttributes, AriaRole, FC, memo } from 'react';

import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';
import { WebTarget } from 'styled-components';

import { HtmlContentContainer } from './HtmlContent.styles';

type Props = {
    content: string;
    isNegative?: boolean;
    className?: string;
    dataTest?: string;
    baseComponent?: WebTarget;
    role?: AriaRole;
};

const HtmlContent: FC<Props & AriaAttributes> = ({
    content,
    baseComponent,
    isNegative,
    className,
    dataTest,
    role,
    ...ariaAttrs
}) => {
    if (!content) {
        return null;
    }

    // CMS-managed content may include iframe embeds (video, partner widgets).
    // eslint-disable-next-line sonarjs/dompurify-unsafe-config
    const sanitizedContent = DOMPurify.sanitize(content, {
        USE_PROFILES: { html: true, svg: true },
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allowfullscreen', 'frameborder', 'target'],
        ALLOW_SELF_CLOSE_IN_ATTR: true,
        ALLOW_ARIA_ATTR: true,
        ALLOW_DATA_ATTR: true,
    });

    return (
        <HtmlContentContainer
            {...ariaAttrs}
            role={role}
            as={baseComponent}
            $negative={!!isNegative}
            className={classNames('html-content', className)}
            data-test={dataTest}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
    );
};

export default memo(HtmlContent);
